import { useState, useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  fetchCampaigns,
  applyNegativeKeywordListToCampaigns,
  listNegativeKeywordListCampaigns,
  removeNegativeKeywordListFromCampaign,
} from '@/lib/apis';
import { CAMPAIGN_STATE, getCampaignType, getCampaignTypeLabel } from '@/lib/types';
import type { CampaignData, NegativeKeywordList } from '@/lib/types';
import { organizationDataAtom } from '@/store';
import { useToast } from '@/hooks/useToast';
import googleAds from '/google-ads.svg';

interface ApplyListToCampaignsModalProps {
  isOpen: boolean;
  onClose: () => void;
  list: NegativeKeywordList;
}

const getStateDisplay = (campaign: CampaignData) => {
  switch (campaign.state) {
    case CAMPAIGN_STATE.LIVE:
      return { text: 'Live', color: 'bg-green-100 text-green-800' };
    case CAMPAIGN_STATE.PAUSED:
      return { text: campaign.first_published_at ? 'Paused' : 'Ready', color: 'bg-amber-100 text-amber-800' };
    case CAMPAIGN_STATE.DRAFT:
      return { text: 'Ready', color: 'bg-amber-100 text-amber-800' };
    default:
      return { text: campaign.state, color: 'bg-gray-100 text-gray-900' };
  }
};

export function ApplyListToCampaignsModal({ isOpen, onClose, list }: ApplyListToCampaignsModalProps) {
  const organizationData = useAtomValue(organizationDataAtom);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // Additions: not-yet-applied campaigns the user checked. Removals: already-applied campaigns the
  // user unchecked (detached on save). Kept separate so save can attach and detach in one action.
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<number[]>([]);
  const [removedCampaignIds, setRemovedCampaignIds] = useState<number[]>([]);

  const { data: campaignsData = [], isLoading: isLoadingCampaigns } = useQuery({
    queryKey: ['apply-list-campaigns', organizationData?.id],
    queryFn: () => fetchCampaigns(organizationData!.id),
    enabled: !!organizationData?.id && isOpen,
  });

  // Campaigns the list is already attached to — pre-checked and not re-applied.
  const { data: appliedData } = useQuery({
    queryKey: ['negative-keyword-list', list.id, 'campaigns'],
    queryFn: () => listNegativeKeywordListCampaigns(list.id),
    enabled: isOpen,
  });

  const appliedCampaignIds = useMemo(
    () => new Set((appliedData?.campaigns ?? []).map((c) => c.campaign_id).filter((id): id is number => id != null)),
    [appliedData],
  );

  // Only campaigns not already attached are selectable.
  const selectableCampaigns = useMemo(
    () => campaignsData.filter((c) => !appliedCampaignIds.has(c.id)),
    [campaignsData, appliedCampaignIds],
  );

  const applyMutation = useMutation({
    mutationFn: async () => {
      if (selectedCampaignIds.length > 0) {
        await applyNegativeKeywordListToCampaigns(list.id, selectedCampaignIds);
      }
      // Detach is per-campaign; run sequentially to avoid hammering Google Ads.
      for (const campaignId of removedCampaignIds) {
        await removeNegativeKeywordListFromCampaign(list.id, campaignId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['negative-keyword-lists'] });
      queryClient.invalidateQueries({ queryKey: ['negative-keyword-list', list.id, 'campaigns'] });
      // Prefix-matches the campaign + ad negative-keyword queries so both views refresh.
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      handleClose();
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to save changes', description: error.message, variant: 'destructive' });
    },
  });

  const handleSelectAll = () => {
    if (selectedCampaignIds.length === selectableCampaigns.length) {
      setSelectedCampaignIds([]);
    } else {
      setSelectedCampaignIds(selectableCampaigns.map((c) => c.id));
    }
  };

  const handleToggle = (campaignId: number) => {
    if (appliedCampaignIds.has(campaignId)) {
      // Applied campaign: toggle whether it's marked for detach.
      setRemovedCampaignIds((prev) =>
        prev.includes(campaignId) ? prev.filter((id) => id !== campaignId) : [...prev, campaignId],
      );
    } else {
      setSelectedCampaignIds((prev) =>
        prev.includes(campaignId) ? prev.filter((id) => id !== campaignId) : [...prev, campaignId],
      );
    }
  };

  const handleClose = () => {
    setSelectedCampaignIds([]);
    setRemovedCampaignIds([]);
    onClose();
  };

  const isAllSelected = selectedCampaignIds.length === selectableCampaigns.length && selectableCampaigns.length > 0;
  const isIndeterminate = selectedCampaignIds.length > 0 && selectedCampaignIds.length < selectableCampaigns.length;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] min-h-[560px] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-gray-900">Apply Negative Keyword List</DialogTitle>
          <DialogDescription>
            Select the campaigns the "{list.name}" list ({list.member_count} keyword{list.member_count !== 1 ? 's' : ''})
            should be attached to. Ads will stop showing for these search terms across the selected campaigns. Lists
            added to draft campaigns are applied automatically when the campaign goes live.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex flex-col space-y-4 overflow-hidden">
          {isLoadingCampaigns ? (
            <div className="text-center py-8 text-gray-500">Loading campaigns...</div>
          ) : campaignsData.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No campaigns available in this organization</div>
          ) : (
            <div className="rounded-lg border bg-white shadow-sm overflow-hidden">
              <div className="max-h-[360px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-white z-10">
                    <TableRow>
                      <TableHead className="py-3 px-4 text-center bg-white">
                        <Checkbox
                          checked={isAllSelected}
                          ref={(ref) => {
                            if (ref) (ref as HTMLInputElement).indeterminate = isIndeterminate;
                          }}
                          onCheckedChange={handleSelectAll}
                          disabled={selectableCampaigns.length === 0}
                        />
                      </TableHead>
                      <TableHead className="py-3 px-4 text-gray-900 bg-white">Campaign</TableHead>
                      <TableHead className="py-3 px-4 text-gray-900 bg-white">Type</TableHead>
                      <TableHead className="py-3 px-4 text-gray-900 bg-white">State</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {campaignsData.map((campaign) => {
                      const alreadyApplied = appliedCampaignIds.has(campaign.id);
                      const isMarkedForRemoval = removedCampaignIds.includes(campaign.id);
                      const isSelected = selectedCampaignIds.includes(campaign.id);
                      // Applied campaigns read as checked until marked for removal; others reflect selection.
                      const isChecked = alreadyApplied ? !isMarkedForRemoval : isSelected;
                      const stateDisplay = getStateDisplay(campaign);
                      const displayName = campaign.name;
                      return (
                        <TableRow
                          key={campaign.id}
                          className={`cursor-pointer hover:bg-gray-50 ${isChecked ? 'bg-[#290BD9]/10' : ''} ${isMarkedForRemoval ? 'opacity-60' : ''}`}
                          onClick={() => handleToggle(campaign.id)}
                        >
                          <TableCell className="py-3 px-4 text-center">
                            <Checkbox
                              checked={isChecked}
                              onCheckedChange={() => handleToggle(campaign.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <div className="flex items-center space-x-3">
                              <img src={googleAds} alt="Google Ads" className="w-6 h-6 flex-shrink-0" />
                              <span className="font-medium text-gray-900">{displayName}</span>
                              {alreadyApplied && (
                                <Badge
                                  variant="outline"
                                  className={isMarkedForRemoval ? 'bg-[#FEE2E2] text-[#B91C1C]' : 'bg-gray-100 text-gray-600'}
                                >
                                  {isMarkedForRemoval ? 'Removing' : 'Applied'}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="py-3 px-4 text-gray-900">
                            {getCampaignTypeLabel(getCampaignType(campaign))}
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <Badge variant="outline" className={stateDisplay.color}>
                              {stateDisplay.text}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {(selectedCampaignIds.length > 0 || removedCampaignIds.length > 0) && (
            <Card className="rounded-lg border bg-blue-50">
              <CardContent className="pt-6 space-y-1">
                {selectedCampaignIds.length > 0 && (
                  <p className="font-medium text-gray-900">
                    Applying "{list.name}" to {selectedCampaignIds.length} campaign
                    {selectedCampaignIds.length !== 1 ? 's' : ''}
                  </p>
                )}
                {removedCampaignIds.length > 0 && (
                  <p className="font-medium text-gray-900">
                    Removing "{list.name}" from {removedCampaignIds.length} campaign
                    {removedCampaignIds.length !== 1 ? 's' : ''}: those campaigns will no longer exclude these keywords.
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={handleClose} disabled={applyMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => applyMutation.mutate()}
            disabled={(selectedCampaignIds.length === 0 && removedCampaignIds.length === 0) || applyMutation.isPending}
            className="bg-[#290BD9] hover:bg-[#2309C5] text-white flex items-center space-x-2"
          >
            <span>{applyMutation.isPending ? 'SAVING...' : 'SAVE CHANGES'}</span>
            {!applyMutation.isPending && <ArrowRight className="h-4 w-4" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
